import { useRef } from 'react';

import html2canvas from 'html2canvas-pro';

type ExtraDownloadData = {
  chatDate: string;
};

export const useScreenshot = (
  fileName: string,
  { chatDate }: ExtraDownloadData,
) => {
  const captureElementRef = useRef<HTMLDivElement>(null);

  const bgDefault = () => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--background-default')
      .trim();
  };

  const svgToUrl = () => {
    const svgs = [
      ...(captureElementRef.current?.querySelectorAll('svg') ?? []),
    ];

    const svg = svgs.find((svg) => Number(svg.getAttribute('width')) > 100); // FYI: The chart legend shouldn't be captured as the first SVG.

    if (!svg) return { svg, url: undefined };

    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svg);

    // If not exists on svg element
    if (!source.includes('xmlns="http://www.w3.org/2000/svg"')) {
      source = source.replace(
        '<svg',
        '<svg xmlns="http://www.w3.org/2000/svg"',
      );
    }

    const blob = new Blob([source], {
      type: 'image/svg+xml;charset=utf-8',
    });

    const url = URL.createObjectURL(blob);

    return { svg, url };
  };

  const onExportPNG = async () => {
    const { url, svg } = svgToUrl();
    if (!(svg || url)) return;
    try {
      const img = new Image();

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = url;
      });

      const legend = captureElementRef.current?.querySelector(
        '[data-legend]',
      ) as HTMLElement | null;
      const legendHeight = legend?.getBoundingClientRect().height ?? 0;

      const canvas = document.createElement('canvas');
      canvas.width = svg.clientWidth;
      canvas.height = svg.clientHeight + legendHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = bgDefault();
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        if (legend) {
          const legendCanvas = await html2canvas(legend, {
            scale: 1,
            backgroundColor: bgDefault(),
          });

          ctx.drawImage(legendCanvas, 0, canvas.height - legendCanvas.height);
        }

        canvas.toBlob((blob) => {
          if (!blob) return;

          const pngUrl = URL.createObjectURL(blob);
          download(pngUrl);
          URL.revokeObjectURL(pngUrl);
        }, 'image/png');
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const download = (img: string | void) => {
    if (img) {
      const a = document.createElement('a');
      a.href = img;
      a.download = `${fileName}-${chatDate}.png`;
      a.click();
    }
  };

  return { captureElementRef, onExportPNG };
};
